/* global React, Icon, Card, Button, Field, Input, useState, t, toast */
// ============================================================
// REGNSKAPSBYRÅ — opprettelse og medarbeidere
// ============================================================
// Onboarding: et byrå er et foretak, og identiteten hentes fra Enhetsregisteret på
// samme måte som når en kunde oppretter foretaket sitt. Backend slår opp
// organisasjonsnummeret selv (`createAccountingFirmHttp`) — navnet kan ikke skrives
// inn her, nettopp for at ingen skal kunne registrere seg som et byrå de ikke er.
//
// Merk at dette IKKE er en representasjonssjekk mot Altinn: oppslaget bekrefter at
// foretaket finnes, ikke at den som oppretter kan opptre på vegne av det. Skal det
// kreves, hører det til sammen med de øvrige signeringsrettighetene.

// Stilling → hva den faktisk gir. Teksten står her fordi den som ansetter må kunne
// se forskjellen før hen velger, ikke etterpå.
const FIRM_TITLES = [
  {
    key: 'administrator',
    label: 'Administrator',
    summary: 'Kan legge til og fjerne medarbeidere og klienter, og jobbe i regnskapene.',
  },
  {
    key: 'kontrollor',
    label: 'Kontrollør',
    summary: 'Kan godkjenne andres arbeid i kontrollflaten, i tillegg til å føre regnskap.',
  },
  {
    key: 'regnskapsforer',
    label: 'Regnskapsfører',
    summary: 'Fører regnskap på klientene hen er satt på, og kan være oppdragsansvarlig.',
  },
  {
    key: 'assistent',
    label: 'Regnskapsassistent',
    summary: 'Fører regnskap på klientene hen er satt på, men kan ikke være oppdragsansvarlig eller kontrollør.',
  },
];

const firmTitleLabel = (key) => (FIRM_TITLES.find((entry) => entry.key === key) || {}).label || '—';

function FirmTitleSelect({ value, onChange, disabled }) {
  const current = FIRM_TITLES.find((entry) => entry.key === value) || FIRM_TITLES[2];
  return <div className="rf-title-select">
    <select
      className="input"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
      aria-label={t('rf.member.title', 'Stilling')}
    >
      {FIRM_TITLES.map((entry) => <option key={entry.key} value={entry.key}>{entry.label}</option>)}
    </select>
    <div className="t-xs muted rf-title-help">{current.summary}</div>
  </div>;
}

// Opprettelsen ligger i onboarding-modalen, sammen med «Opprett foretak» og
// «Opprett Privatøkonomi». Et byrå er noe man oppretter én gang, i samme flyt og med
// samme uttrykk som de andre — ikke et løst skjema på en tom side.
function FirmOnboardingDialog({ onClose }) {
  const [organizationNumber, setOrganizationNumber] = useState('');
  const [firmName, setFirmName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);
  const digits = organizationNumber.replace(/\D/g, '');
  // Demokontoen setter identiteten selv, uten registeroppslag. For alle andre er
  // navnet backendens, og feltet ville bare vaert villedende.
  const isDemo = window.PlummeBackend?.isDemoAccount === true;
  // Settes naar backend sier at registeret ikke fant foretaket, men tillater at det
  // opprettes likevel. Da — og bare da — ma navnet oppgis her.
  const [needsName, setNeedsName] = useState(false);
  const nameRequired = isDemo || needsName;
  const canSubmit = digits.length === 9 && (!nameRequired || firmName.trim().length > 1) && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError('');
    try {
      const result = await window.PlummeBackend?.createAccountingFirm?.({
        organizationNumber: digits,
        country: 'NO',
        ...(nameRequired ? { firmName: firmName.trim() } : {}),
      });
      // Uten dette vet resten av appen fortsatt ikke at brukeren har et byraa.
      await window.PlummeBackend?.refreshFirmMemberships?.();
      setCreated(result?.firm || { firmName: firmName.trim() });
    } catch (createError) {
      if (createError?.domainCode === 'firm.registryNameRequired') {
        setNeedsName(true);
        setError(t('rf.create.nameRequired', 'Fant ikke foretaket i Enhetsregisteret. Skriv inn navnet på byrået for å fortsette.'));
        return;
      }
      setError(createError?.message || String(createError));
    } finally {
      setBusy(false);
    }
  };

  const openFirmSurface = () => {
    onClose();
    window.dispatchEvent(new CustomEvent('plumme-open-firm-surface'));
  };

  return <div className="ob-scrim" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <div className="ob-card" role="dialog" aria-label={t('rf.create.title', 'Opprett regnskapsbyrå')}>
      <button className="ob-close" onClick={onClose} aria-label={t('common.close', 'Lukk')}><Icon name="x" size={18} /></button>
      <div className="ob-stage">
        {created
          ? <div className="ob-hero">
            <img className="ob-hero-plum" src="src/img/PlummaCheer.png" alt="" />
            <div className="ob-eyebrow">{t('rf.create.eyebrow', 'Regnskapsbyrå')}</div>
            <h2 className="ob-title">{created.firmName || t('rf.create.done', 'Regnskapsbyrået er opprettet')}</h2>
            <p className="ob-lead">{t('rf.create.readyBody', 'Byrået er opprettet, og du er eier. Neste steg er å invitere medarbeiderne dine og knytte til klienter.')}</p>
          </div>
          : <div>
            <div className="ob-eyebrow">{t('rf.create.eyebrow', 'Regnskapsbyrå')}</div>
            <h2 className="ob-title">{t('rf.create.title', 'Opprett regnskapsbyrå')}</h2>
            <p className="ob-lead">{t('rf.create.body', 'Byrået hentes fra Enhetsregisteret. Du blir eier og kan invitere medarbeidere etterpå.')}</p>
            <div className="ob-fields">
              <Field label={t('rf.create.orgNumber', 'Organisasjonsnummer')}>
                <Input
                  value={organizationNumber}
                  onChange={(event) => { setError(''); setOrganizationNumber(event.target.value); }}
                  placeholder="123 456 789"
                  inputMode="numeric"
                  onKeyDown={(event) => { if (event.key === 'Enter') void submit(); }}
                />
              </Field>
              {/* En gra knapp uten forklaring ser ut som at noe er ødelagt. */}
              <div className="ob-lookup-note">
                {digits.length === 9
                  ? (isDemo
                    ? t('rf.create.hintDemo', 'Demokontoen kan bruke et oppdiktet nummer. Gi byrået et navn under.')
                    : t('rf.create.hintReady', 'Nummeret hentes fra Enhetsregisteret når du oppretter.'))
                  : t('rf.create.hintDigits', 'Ni siffer. Byrået må være registrert i Enhetsregisteret.')}
              </div>
              {nameRequired ? <Field label={t('rf.create.firmName', 'Navn på byrået')}>
                <Input value={firmName} onChange={(event) => setFirmName(event.target.value)} placeholder="Demobyrå AS" />
              </Field> : null}
              {needsName ? <div className="ob-lookup-note">
                {t('rf.create.unverifiedNote', 'Byrået opprettes uten registeroppslag og merkes som uverifisert. Det må verifiseres før ekte byråer tas i bruk.')}
              </div> : null}
              {error ? <div className="ob-warning ob-warning-error"><Icon name="alertTri" size={17} /><span>{error}</span></div> : null}
              <div className="ob-warning">
                <Icon name="info" size={17} />
                <span>{t('rf.create.note', 'Oppslaget bekrefter at foretaket finnes. Det kontrollerer ikke om du kan opptre på vegne av det.')}</span>
              </div>
            </div>
          </div>}
      </div>
      <div className="ob-nav">
        <button className="ob-skip" onClick={onClose}>{created ? t('common.close', 'Lukk') : t('common.cancel', 'Avbryt')}</button>
        {created
          ? <Button icon="arrowRight" onClick={openFirmSurface} style={{ marginLeft: 'auto' }}>{t('rf.create.openSurface', 'Åpne byråflaten')}</Button>
          : <Button icon="check" disabled={!canSubmit} onClick={() => void submit()} style={{ marginLeft: 'auto' }}>
            {busy ? t('rf.create.working', 'Oppretter …') : t('rf.create.submit', 'Opprett byrå')}
          </Button>}
      </div>
    </div>
  </div>;
}

// Medarbeidere: invitere, endre stilling, og fjerne tilgang. Fjerning trekker samtidig
// all klienttilgang byrået har gitt personen — det skjer i backend, i én operasjon.
function RfMembers({ firmId, members, canManage, onChanged }) {
  const [email, setEmail] = useState('');
  const [title, setTitle] = useState('regnskapsforer');
  const [busy, setBusy] = useState('');

  const invite = async () => {
    if (!email.trim()) return;
    setBusy('invite');
    try {
      const result = await window.PlummeBackend?.inviteAccountingFirmMember?.({ firmId, email: email.trim(), title });
      setEmail('');
      toast(
        result?.emailDeliveryStatus === 'failed'
          ? t('rf.member.inviteMailFailed', 'Invitasjonen er opprettet, men e-posten kunne ikke sendes.')
          : t('rf.member.invited', 'Invitasjon sendt'),
        result?.emailDeliveryStatus === 'failed' ? 'warning' : 'success',
      );
      if (onChanged) await onChanged();
    } catch (error) {
      toast(error?.message || String(error), 'error');
    } finally {
      setBusy('');
    }
  };

  const changeTitle = async (member, nextTitle) => {
    setBusy(member.userId);
    try {
      await window.PlummeBackend?.updateAccountingFirmMember?.({ firmId, memberUserId: member.userId, title: nextTitle });
      toast(t('rf.member.updated', 'Stillingen er endret'), 'success');
      if (onChanged) await onChanged();
    } catch (error) {
      toast(error?.message || String(error), 'error');
    } finally {
      setBusy('');
    }
  };

  const removeMember = async (member) => {
    const question = t('rf.member.removeConfirm', 'Fjern tilgangen til {name}? All klienttilgang byrået har gitt denne personen trekkes samtidig.')
      .replace('{name}', member.email || member.userId);
    if (!window.confirm(question)) return;
    setBusy(member.userId);
    try {
      const result = await window.PlummeBackend?.removeAccountingFirmMember?.({ firmId, memberUserId: member.userId });
      const revoked = (result?.revoked || []).filter((entry) => entry.status === 'revoked').length;
      toast(
        revoked
          ? t('rf.member.removedWithClients', 'Tilgangen er fjernet, også hos {count} klienter.').replace('{count}', String(revoked))
          : t('rf.member.removed', 'Tilgangen er fjernet'),
        'success',
      );
      if (onChanged) await onChanged();
    } catch (error) {
      toast(error?.message || String(error), 'error');
    } finally {
      setBusy('');
    }
  };

  return <div className="col gap-16">
    {canManage ? <Card className="kl-link">
      <div className="kl-section-hd" style={{ marginBottom: 14 }}>{t('rf.member.inviteTitle', 'Inviter en medarbeider')}</div>
      <div className="md-invite-row">
        <Field label={t('rf.member.email', 'E-post')} className="grow">
          <Input
            type="email"
            value={email}
            placeholder="navn@byra.no"
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>
        <Field label={t('rf.member.title', 'Stilling')}>
          <FirmTitleSelect value={title} onChange={setTitle} disabled={busy === 'invite'} />
        </Field>
        <Button size="sm" disabled={!email.trim() || busy === 'invite'} onClick={() => void invite()}>
          {busy === 'invite' ? t('rf.member.inviting', 'Sender …') : t('rf.member.invite', 'Send invitasjon')}
        </Button>
      </div>
    </Card> : null}

    <Card className="md-list" pad={false}>
      <div className="kl-section-hd md-list-hd">{t('rf.member.listTitle', 'Medarbeidere')}</div>
      <div className="md-table">
        <div className="md-row md-row-head">
          <div>{t('rf.member.person', 'Person')}</div>
          <div>{t('rf.member.title', 'Stilling')}</div>
          <div>{t('rf.member.rights', 'Rettigheter')}</div>
          <div />
        </div>
        {members.map((member) => {
          const isOwner = member.firmRole === 'owner';
          return <div className="md-row" key={member.userId}>
            <div className="md-cell-person">{member.email || member.userId}</div>
            <div className="md-cell-role">
              {canManage && !isOwner
                ? <FirmTitleSelect
                  value={member.title || 'regnskapsforer'}
                  onChange={(next) => void changeTitle(member, next)}
                  disabled={busy === member.userId}
                />
                : firmTitleLabel(member.title)}
            </div>
            <div className="md-cell-rights">
              {isOwner ? <span className="md-tag">{t('rf.member.ownerRights', 'Eier av byrået')}</span> : null}
              {(member.capabilities || []).includes('kontrollor')
                ? <span className="md-tag">{t('rf.member.canControl', 'Kan kontrollere')}</span>
                : null}
            </div>
            <div className="md-cell-acts">
              {canManage && !isOwner
                ? <button
                  type="button"
                  className="md-remove"
                  disabled={busy === member.userId}
                  onClick={() => void removeMember(member)}
                >{t('rf.member.remove', 'Fjern tilgang')}</button>
                : null}
            </div>
          </div>;
        })}
      </div>
      {members.length === 0
        ? <div className="md-empty">{t('rf.member.none', 'Ingen medarbeidere ennå.')}</div>
        : null}
      {canManage
        ? <div className="md-footnote">
          {t('rf.member.removeNote', 'Når du fjerner en medarbeider, trekkes også all klienttilgang byrået har gitt personen — i alle klienter, i én operasjon.')}
        </div>
        : null}
    </Card>
  </div>;
}

Object.assign(window, { FirmOnboardingDialog, RfMembers, FirmTitleSelect, FIRM_TITLES, firmTitleLabel });
